import type { IotApiClient, Nullable } from "@iot-platform/core";
import { coreTypes, FallbackError } from "@iot-platform/core";
import type { CurrentUserDto } from "@user/data/dto/current-user.dto";
import type { UserDto, UsersDto } from "@user/data/dto/user.dto";
import { AuthenticatedUserMapper } from "@user/data/mappers/authenticated-user.mapper";
import { UserMapper } from "@user/data/mappers/user.mapper";
import type { AuthenticatedUser } from "@user/domain/models/authenticated-user.model";
import type {
    User,
    UserCreate,
    UserEdit,
    UserResume,
    Users,
} from "@user/domain/models/user.model";
import { inject, injectable } from "inversify";
import { err, ok, ResultAsync } from "neverthrow";

@injectable()
export class UserDatasource {
    static readonly USERS_PATH = "/operations/logistics/users";
    static readonly USERS_ADMIN_PATH = "/admin/users";

    constructor(
        @inject(UserMapper)
        private readonly userMapper: UserMapper,
        @inject(coreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(AuthenticatedUserMapper)
        private readonly authenticatedUserMapper: AuthenticatedUserMapper,
    ) {}

    fetchAllResume(): ResultAsync<Users, FallbackError> {
        return this.iotApiClient.http
            .get<UsersDto>(UserDatasource.USERS_ADMIN_PATH)
            .mapErr(() => new FallbackError())
            .map((response) => this.userMapper.mapList(response.data));
    }

    /**
     * Looks up a user by email without exposing the rest of the user listing.
     * The email is expected to be normalized (trimmed and lowercased) and the
     * match is done client-side as well, in case the backend ignores the
     * `email` query filter and returns the full list.
     */
    fetchResumeByEmail(
        email: string,
    ): ResultAsync<Nullable<UserResume>, FallbackError> {
        return this.iotApiClient.http
            .get<UsersDto>(UserDatasource.USERS_ADMIN_PATH, {
                query: { email },
            })
            .mapErr(() => new FallbackError())
            .map((response) => {
                const matchingUserDto = response.data.find(
                    (userDto) => userDto.email.trim().toLowerCase() === email,
                );

                return matchingUserDto
                    ? this.userMapper.mapResume(matchingUserDto)
                    : null;
            });
    }

    create(createUser: UserCreate): ResultAsync<User, FallbackError> {
        const userBody = this.userMapper.mapToCreateBody(createUser);

        return this.iotApiClient.http
            .post<UserDto>(UserDatasource.USERS_PATH, userBody)
            .mapErr(() => new FallbackError())
            .andThen((response) => {
                const createdUser = this.userMapper.map(response.data);

                if (!createdUser) return err(new FallbackError());

                return ok(createdUser);
            });
    }

    edit(user: UserEdit): ResultAsync<User, FallbackError> {
        const userBody = this.userMapper.mapToEditBody(user);

        return this.iotApiClient.http
            .patch<UserDto>(
                `${UserDatasource.USERS_PATH}/${user.id}/`,
                userBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) => {
                const editedUser = this.userMapper.map(response.data);

                if (!editedUser) return err(new FallbackError());

                return ok(editedUser);
            });
    }

    delete(userId: number): ResultAsync<boolean, FallbackError> {
        return this.iotApiClient.http
            .delete(`${UserDatasource.USERS_PATH}/${userId}/`)
            .mapErr(() => new FallbackError())
            .andThen(() => ok(true));
    }

    fetchById(userId: number): ResultAsync<User, FallbackError> {
        return this.iotApiClient.http
            .get<UserDto>(`${UserDatasource.USERS_PATH}/${userId}/`)
            .mapErr(() => new FallbackError())
            .andThen((response) => {
                const user = this.userMapper.map(response.data);

                if (!user) return err(new FallbackError());

                return ok(user);
            });
    }

    fetchCurrentUser(): ResultAsync<AuthenticatedUser, FallbackError> {
        this.iotApiClient.enableAuth();

        return this.iotApiClient.http
            .get<CurrentUserDto>("/users/current/")
            .mapErr(() => {
                this.iotApiClient.disableAuth();

                return new FallbackError();
            })
            .andThen((response) => {
                const user = this.authenticatedUserMapper.map(response.data);

                if (!user) {
                    this.iotApiClient.disableAuth();
                    return err(new FallbackError());
                }

                return ok(user);
            });
    }
}
